/**
 * Node Layout Helpers
 * ====================
 * Single source of truth for node row layout, widget resolution, and port positioning.
 *
 * Converts a node definition (inputs, outputs, params) into an ordered array of
 * row descriptors. Each row knows its height, widget type, and (for ports) its side.
 *
 * Used by:
 *   - ProcessingNodeItemType (render + getPorts)
 *   - ThreadPorts (visual port positioning)
 *   - threadGeometry (edge endpoint math)
 */

import {
  resolveCssFontFamily,
  resolveCssLengthPx,
} from '../utils/cssTypographyResolver';
import {
  measureControlLayoutHeight,
  resolveControlLayout,
  resolveSliderInlineColumnLayout,
} from '../utils/controlLayout';
import { PortType } from './portTypes';
import { getMediaEntryParamFacts } from './mediaEntryParams';

// Matches `.node-card-header min-height` in nodeCard.css
export const HEADER_HEIGHT = 40;

// Control heights by widget type. Field row heights are resolved through
// resolveNodeRowHeight so the label stack and DOM row box stay in one contract.
const CONTROL_HEIGHT = {
  'port-anchor': 0,
};

export const TEXTAREA_WIDGETS = new Set(['textarea', 'prompt-editor']);

// The definition row's id is the data key it reads (`item.data.nodeId`).
export const NODE_DEFINITION_ROW_ID = 'nodeId';

const CONTROL_SURFACE_WIDGETS = new Set([
  'select',
  'text-input',
  'textarea',
  'number-input',
  'prompt-editor',
  'image-drop',
]);

const NODE_LAYOUT_METRIC_FALLBACKS = {
  spaceXs: 4,
  spaceSm: 8,
  spaceMd: 12,
  labelFontSize: 12,
  titleFontSize: 14,
  valueFontSize: 12,
  captionFontSize: 11,
  controlBorderWidth: 1,
  colorInputSize: 32,
  inlineTextareaMinHeight: 36,
  cardRadius: 12,
  iconSize: 14,
  previewRadius: 8,
  controlRadiusSm: 4,
  controlRadiusMd: 8,
  colorInputSwatchRadius: 2,
  sliderTrackHitArea: 16,
  sliderCompactTrackHeight: 2,
  sliderCompactThumbSize: 12,
  sliderThumbBorderWidth: 2,
  sliderInlineLabelMinWidth: 48,
  sliderInlineLabelWidth: 84,
  sliderInlineTrackMinWidth: 70,
  sliderInlineValueWidth: 42,
  sliderInlineValueHeight: 24,
  sliderInlineGap: 8,
  toggleCompactTrackWidth: 28,
  toggleCompactTrackHeight: 14,
  toggleCompactThumbSize: 10,
  toggleTrackPadding: 2,
  referenceChipHeight: 28,
  referenceChipThumbSize: 26,
  referenceChipContentGap: 4,
  referenceChipInterChipGap: 4,
  referenceChipThumbRadiusStart: 3,
  referenceChipThumbRadiusEnd: 0,
  generatorReservedToolbarHeight: 56,
  typedPreviewSummaryHeight: 48,
  mediaDropRowHeight: 36,
};

let cachedNodeLayoutMetrics = null;

function resolveLengthPx(cssLength, fallbackPx, options) {
  return resolveCssLengthPx(cssLength, fallbackPx, options);
}

function getPositiveInteger(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }
  return Math.floor(parsed);
}

function getDefaultTextAreaRows(row) {
  if (row?.widget === 'prompt-editor') return 4;
  if (row?.widget === 'text-input') return 1;
  return 2;
}

export function getTextAreaRows(row, fallback = getDefaultTextAreaRows(row)) {
  return getPositiveInteger(row?.graphWidget?.rows ?? row?.rows, fallback);
}

export function usesAdaptiveRows(row) {
  return row?.graphWidget?.adaptiveRows === true || row?.adaptiveRows === true;
}

export function isTextAreaControlRow(row) {
  if (!row) return false;
  if (TEXTAREA_WIDGETS.has(row.widget)) return true;
  if (row.widget !== 'text-input') return false;
  return getTextAreaRows(row, 1) > 1 || usesAdaptiveRows(row);
}

function createNodeLayoutProbe(className, attributes = {}) {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return null;
  }

  const mount = document.body || document.documentElement;
  if (!mount) return null;

  const probe = document.createElement('div');
  probe.className = className;
  Object.entries(attributes).forEach(([name, value]) => {
    probe.setAttribute(name, value);
  });
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  mount.appendChild(probe);
  return probe;
}

function createCompactSliderProbe() {
  // Slider geometry tokens are scoped to the control; labelled node rows resolve compact/inline.
  return createNodeLayoutProbe('slider-control', {
    'data-density': 'compact',
    'data-layout': 'inline',
    'data-thumb-style': 'solid',
  });
}

function resolveScopedLengthPx(probe, cssLength, fallbackPx, cacheKey, options = {}) {
  if (!probe) return fallbackPx;
  return resolveLengthPx(cssLength, fallbackPx, {
    ...options,
    contextElement: probe,
    cacheKey,
  });
}

/**
 * Measure a probe's real rendered box height (device-independent CSS px). Used to
 * make the node metric TRACK the real CSS boxes (the select trigger, the param
 * label) instead of a formula that undercounts them — the fixed-geometry rows are
 * content-independent, so the measurement is stable. Returns the fallback when the
 * probe cannot lay out (headless/jsdom `offsetHeight === 0`), so tests keep the
 * deterministic formula values.
 */
function measureNodeLayoutProbeHeight(probe, fallbackPx) {
  if (!probe) return fallbackPx;
  const rect = typeof probe.getBoundingClientRect === 'function' ? probe.getBoundingClientRect() : null;
  const measured = (rect && rect.height) || probe.offsetHeight || 0;
  return Number.isFinite(measured) && measured > 0 ? measured : fallbackPx;
}

function resolveNodeLayoutMetrics() {
  const sliderProbe = createCompactSliderProbe();
  const toggleProbe = createNodeLayoutProbe('toggle-switch', { 'data-compact': 'true' });
  const referenceChipProbe = createNodeLayoutProbe('reference-chip reference-chip--compact');
  const referenceChipStripProbe = createNodeLayoutProbe('reference-chip-strip');
  const mediaDropZoneProbe = createNodeLayoutProbe('prompt-reference-dropzone');
  const generatorProbe = createNodeLayoutProbe('node-card node-card--generator');
  // Boxes whose real rendered height is driven by control-specific tokens the
  // formula cannot reconstruct (select trigger, numeric field, param label).
  // Measure the same classes the card mounts so the pinned metric cannot clip rows.
  const selectTriggerProbe = createNodeLayoutProbe('select-trigger');
  if (selectTriggerProbe) selectTriggerProbe.textContent = 'Ag';
  const paramLabelProbe = createNodeLayoutProbe('param-label');
  if (paramLabelProbe) paramLabelProbe.textContent = 'Ag';
  // A node number field is DraggableNumberInput, not a generic text field. Measure its
  // actual wrapper under node chrome instead of inferring its box from font-size * 1.2.
  const numberInputProbe = createNodeLayoutProbe('node-card');
  let numberInputControlProbe = null;
  if (numberInputProbe && typeof document !== 'undefined') {
    const rowEl = document.createElement('div');
    rowEl.className = 'node-card-row node-card-row--field node-card-row--param';
    const fieldEl = document.createElement('div');
    const controlEl = document.createElement('div');
    controlEl.className = 'draggable-number-input';
    const inputWrapEl = document.createElement('div');
    inputWrapEl.className = 'draggable-number-input__input-wrap';
    const inputEl = document.createElement('input');
    inputEl.className = 'ui-text-input draggable-number-input__field module-input node-inline-number-input';
    inputEl.type = 'text';
    inputEl.value = '1024';
    inputWrapEl.appendChild(inputEl);
    controlEl.appendChild(inputWrapEl);
    fieldEl.appendChild(controlEl);
    rowEl.appendChild(fieldEl);
    numberInputProbe.appendChild(rowEl);
    numberInputControlProbe = controlEl;
  }
  // The inline slider ROW height is taller than any single slot formula (the
  // editable-value field renders taller than the raw --slider-inline-value-height
  // token via its border + mono line box). Build a faithful inline row and measure
  // its real height so the slider row height == the DOM.
  const sliderValueProbe = createNodeLayoutProbe('slider-control', {
    'data-density': 'compact',
    'data-layout': 'inline',
    'data-thumb-style': 'solid',
  });
  let sliderInlineRowProbe = null;
  if (sliderValueProbe && typeof document !== 'undefined') {
    const rowEl = document.createElement('div');
    rowEl.className = 'slider-inline-row';
    const labelEl = document.createElement('label');
    labelEl.className = 'slider-inline-label';
    labelEl.textContent = 'Ag';
    const trackWrapEl = document.createElement('div');
    trackWrapEl.className = 'slider-track-wrap';
    const valueCell = document.createElement('span');
    valueCell.className = 'slider-inline-value-cell';
    // Mirror the real Slider value structure (wrapper + input-wrap + field) so the
    // probe box height matches the DOM exactly.
    const valueWrap = document.createElement('div');
    valueWrap.className = 'slider-editable-value';
    const valueInputWrap = document.createElement('div');
    valueInputWrap.className = 'slider-editable-value__wrap';
    const fieldEl = document.createElement('input');
    fieldEl.className = 'slider-editable-value__field';
    fieldEl.setAttribute('value', '0.0');
    valueInputWrap.appendChild(fieldEl);
    valueWrap.appendChild(valueInputWrap);
    valueCell.appendChild(valueWrap);
    rowEl.append(labelEl, trackWrapEl, valueCell);
    sliderValueProbe.appendChild(rowEl);
    sliderInlineRowProbe = rowEl;
  }

  try {
    const spaceXs = resolveLengthPx('var(--space-xs)', NODE_LAYOUT_METRIC_FALLBACKS.spaceXs);
    const spaceSm = resolveLengthPx('var(--space-sm)', NODE_LAYOUT_METRIC_FALLBACKS.spaceSm);
    const spaceMd = resolveLengthPx('var(--space-md)', NODE_LAYOUT_METRIC_FALLBACKS.spaceMd);
    // The regular-card spacing contract (component-tokens/node-card.css). nodeCard.css reads
    // the same names, so DOM, raster, ports and hit regions cannot drift apart.
    const cardInset = resolveLengthPx(
      'var(--ui-node-card-inset)',
      NODE_LAYOUT_METRIC_FALLBACKS.spaceMd,
    );
    const cardSectionGap = resolveLengthPx(
      'var(--ui-node-card-section-gap)',
      NODE_LAYOUT_METRIC_FALLBACKS.spaceSm,
    );
    const cardLabelGap = resolveLengthPx(
      'var(--ui-node-card-label-gap)',
      NODE_LAYOUT_METRIC_FALLBACKS.spaceXs,
    );
    const cardBorderWidth = resolveLengthPx(
      'var(--ui-node-card-border-width)',
      NODE_LAYOUT_METRIC_FALLBACKS.controlBorderWidth,
    );
    const cardDividerWidth = resolveLengthPx(
      'var(--ui-node-card-divider-width)',
      NODE_LAYOUT_METRIC_FALLBACKS.controlBorderWidth,
    );
    const labelSize = resolveLengthPx('var(--label-param-size)', NODE_LAYOUT_METRIC_FALLBACKS.labelFontSize);
    const valueSize = resolveLengthPx(
      'var(--ui-control-surface-font-size)',
      NODE_LAYOUT_METRIC_FALLBACKS.valueFontSize,
    );
    const titleSize = resolveLengthPx(
      'var(--ui-section-title-font-size)',
      NODE_LAYOUT_METRIC_FALLBACKS.titleFontSize,
    );
    const captionSize = resolveLengthPx('var(--text-caption)', NODE_LAYOUT_METRIC_FALLBACKS.captionFontSize);
    const controlBorderWidth = resolveLengthPx('1px', NODE_LAYOUT_METRIC_FALLBACKS.controlBorderWidth);
    const labelLineHeight = labelSize * 1.2;
    const controlLineHeight = valueSize * 1.2;
    const inlineControlPaddingBlock = spaceXs;
    const inlineControlPaddingInline = spaceSm;
    const selectPaddingBlock = spaceSm;
    const selectPaddingInline = spaceMd;
    const colorInputSize = resolveLengthPx(
      'var(--ui-color-input-size)',
      NODE_LAYOUT_METRIC_FALLBACKS.colorInputSize,
    );
    const inlineTextareaMinHeight = resolveLengthPx(
      'calc(var(--space-2xl) + var(--space-xs))',
      NODE_LAYOUT_METRIC_FALLBACKS.inlineTextareaMinHeight,
    );
    const sliderTrackHitArea = resolveScopedLengthPx(
      sliderProbe,
      'var(--slider-track-hit-area)',
      NODE_LAYOUT_METRIC_FALLBACKS.sliderTrackHitArea,
      'node-layout:slider-control:compact-solid:track-hit-area',
    );
    const sliderCompactTrackHeight = resolveScopedLengthPx(
      sliderProbe,
      'var(--slider-track-height)',
      NODE_LAYOUT_METRIC_FALLBACKS.sliderCompactTrackHeight,
      'node-layout:slider-control:compact-solid:track-height',
    );
    const sliderCompactThumbSize = resolveScopedLengthPx(
      sliderProbe,
      'var(--slider-thumb-size)',
      NODE_LAYOUT_METRIC_FALLBACKS.sliderCompactThumbSize,
      'node-layout:slider-control:compact-solid:thumb-size',
    );
    const sliderThumbBorderWidth = resolveScopedLengthPx(
      sliderProbe,
      'var(--slider-thumb-border-width, 2px)',
      NODE_LAYOUT_METRIC_FALLBACKS.sliderThumbBorderWidth,
      'node-layout:slider-control:compact-solid:thumb-border-width',
    );
    const sliderInlineGap = resolveScopedLengthPx(
      sliderProbe,
      'var(--slider-inline-gap)',
      NODE_LAYOUT_METRIC_FALLBACKS.sliderInlineGap,
      'node-layout:slider-control:compact-inline:gap',
    );
    const sliderInlineLabelMinWidth = resolveScopedLengthPx(
      sliderProbe,
      'var(--slider-inline-label-min-width)',
      NODE_LAYOUT_METRIC_FALLBACKS.sliderInlineLabelMinWidth,
      'node-layout:slider-control:compact-inline:label-min-width',
    );
    const sliderInlineLabelWidth = resolveScopedLengthPx(
      sliderProbe,
      'var(--slider-inline-label-width)',
      NODE_LAYOUT_METRIC_FALLBACKS.sliderInlineLabelWidth,
      'node-layout:slider-control:compact-inline:label-width',
    );
    const sliderInlineTrackMinWidth = resolveScopedLengthPx(
      sliderProbe,
      'var(--slider-inline-track-min-width)',
      NODE_LAYOUT_METRIC_FALLBACKS.sliderInlineTrackMinWidth,
      'node-layout:slider-control:compact-inline:track-min-width',
    );
    const sliderInlineValueWidth = resolveScopedLengthPx(
      sliderProbe,
      'var(--slider-inline-value-width)',
      NODE_LAYOUT_METRIC_FALLBACKS.sliderInlineValueWidth,
      'node-layout:slider-control:compact-inline:value-width',
    );
    const sliderInlineValueHeight = resolveScopedLengthPx(
      sliderProbe,
      'var(--slider-inline-value-height)',
      NODE_LAYOUT_METRIC_FALLBACKS.sliderInlineValueHeight,
      'node-layout:slider-control:compact-inline:value-height',
    );
    // The measured real inline-slider control (row) height — the tallest slot, used
    // directly for the inline slider row height so it == the DOM. Falls back to the
    // raw value token in headless/jsdom (keeps the deterministic formula there).
    const sliderInlineControlHeight = measureNodeLayoutProbeHeight(
      sliderInlineRowProbe,
      sliderInlineValueHeight,
    );
    const toggleCompactTrackWidth = resolveScopedLengthPx(
      toggleProbe,
      'var(--toggle-track-width)',
      NODE_LAYOUT_METRIC_FALLBACKS.toggleCompactTrackWidth,
      'node-layout:toggle-switch:compact:track-width',
    );
    const toggleCompactTrackHeight = resolveScopedLengthPx(
      toggleProbe,
      'var(--toggle-track-height)',
      NODE_LAYOUT_METRIC_FALLBACKS.toggleCompactTrackHeight,
      'node-layout:toggle-switch:compact:track-height',
    );
    const toggleCompactThumbSize = resolveScopedLengthPx(
      toggleProbe,
      'var(--toggle-thumb-size)',
      NODE_LAYOUT_METRIC_FALLBACKS.toggleCompactThumbSize,
      'node-layout:toggle-switch:compact:thumb-size',
    );
    const toggleTrackPadding = resolveScopedLengthPx(
      toggleProbe,
      'var(--toggle-track-padding)',
      NODE_LAYOUT_METRIC_FALLBACKS.toggleTrackPadding,
      'node-layout:toggle-switch:compact:track-padding',
    );
    const referenceChipHeight = resolveScopedLengthPx(
      referenceChipProbe,
      'var(--reference-chip-height)',
      NODE_LAYOUT_METRIC_FALLBACKS.referenceChipHeight,
      'node-layout:reference-chip:compact:height',
    );
    const referenceChipThumbSize = resolveScopedLengthPx(
      referenceChipProbe,
      'var(--reference-chip-thumb-size)',
      NODE_LAYOUT_METRIC_FALLBACKS.referenceChipThumbSize,
      'node-layout:reference-chip:compact:thumb-size',
    );
    const referenceChipContentGap = resolveScopedLengthPx(
      referenceChipProbe,
      'var(--reference-chip-gap)',
      NODE_LAYOUT_METRIC_FALLBACKS.referenceChipContentGap,
      'node-layout:reference-chip:compact:content-gap',
    );
    const referenceChipInterChipGap = resolveScopedLengthPx(
      referenceChipStripProbe,
      'var(--reference-chip-inter-chip-gap)',
      NODE_LAYOUT_METRIC_FALLBACKS.referenceChipInterChipGap,
      'node-layout:reference-chip-strip:gap',
    );
    const referenceChipThumbRadiusStart = resolveScopedLengthPx(
      referenceChipProbe,
      'var(--reference-chip-thumb-radius-start)',
      NODE_LAYOUT_METRIC_FALLBACKS.referenceChipThumbRadiusStart,
      'node-layout:reference-chip:compact:thumb-radius-start',
    );
    const referenceChipThumbRadiusEnd = resolveScopedLengthPx(
      referenceChipProbe,
      'var(--reference-chip-thumb-radius-end)',
      NODE_LAYOUT_METRIC_FALLBACKS.referenceChipThumbRadiusEnd,
      'node-layout:reference-chip:compact:thumb-radius-end',
      { allowZero: true },
    );
    // A media picker row's floor is the shared drop zone's own single chip row: the smallest
    // thing a FILLED zone shows. The empty floor and a preview are taller and measured, so this
    // never holds a chip-only zone (a video source) over dead space the way a fixed 48 did.
    const mediaDropRowHeight = resolveScopedLengthPx(
      mediaDropZoneProbe,
      'var(--prompt-reference-single-chip-row-height)',
      NODE_LAYOUT_METRIC_FALLBACKS.mediaDropRowHeight,
      'node-layout:prompt-reference-dropzone:single-chip-row-height',
    );
    const generatorReservedToolbarHeight = resolveScopedLengthPx(
      generatorProbe,
      'var(--node-generator-reserved-toolbar-height)',
      NODE_LAYOUT_METRIC_FALLBACKS.generatorReservedToolbarHeight,
      'node-layout:generator:reserved-toolbar-height',
    );
    const typedPreviewSummaryHeight = resolveScopedLengthPx(
      generatorProbe,
      'var(--node-typed-preview-summary-height)',
      NODE_LAYOUT_METRIC_FALLBACKS.typedPreviewSummaryHeight,
      'node-layout:generator:typed-preview-summary-height',
    );
    const defaultSliderLayout = resolveControlLayout({
      type: 'slider',
      label: 'Slider',
    });
    const sliderControlHeight = measureControlLayoutHeight(defaultSliderLayout, {
      slotHeights: {
        label: labelLineHeight,
        track: sliderTrackHitArea,
        value: sliderInlineControlHeight,
      },
    });
    const inlineControlHeight = controlLineHeight
      + (inlineControlPaddingBlock * 2)
      + (controlBorderWidth * 2);
    const selectControlHeight = controlLineHeight
      + (selectPaddingBlock * 2)
      + (controlBorderWidth * 2);
    // Real boxes (formula is the headless/test fallback). The select trigger's real
    // padding tokens (--ui-select-trigger-padding-block) and the param label's real
    // font line box are taller than the generic formula, so measure them.
    const measuredSelectControlHeight = measureNodeLayoutProbeHeight(selectTriggerProbe, selectControlHeight);
    const measuredNumberInputControlHeight = measureNodeLayoutProbeHeight(numberInputControlProbe, inlineControlHeight);
    const measuredLabelBoxHeight = measureNodeLayoutProbeHeight(paramLabelProbe, labelLineHeight);
    // Node field labels take the card label gap as margin-bottom (nodeCard.css).
    const labelStackHeight = measuredLabelBoxHeight + cardLabelGap;

    const controlHeights = Object.freeze({
      // ToggleSwitch's compact label is inline with its track, so the row is exactly
      // that line. Space around it belongs to the row stack, not to the control.
      toggle: Math.max(toggleCompactTrackHeight, captionSize),
      'image-drop': mediaDropRowHeight,
      'port-anchor': CONTROL_HEIGHT['port-anchor'],
      select: measuredSelectControlHeight,
      'text-input': inlineControlHeight,
      'number-input': measuredNumberInputControlHeight,
      'color-input': colorInputSize,
      slider: sliderControlHeight,
    });

    const row = Object.freeze({
      // Card chrome around the row stack: the card border on both block edges and the
      // block divider (under the header, above a preview). Row tops and the card height include it, so the DOM,
      // the raster walk and the metric height put every row at the same y.
      cardBorderWidth,
      dividerWidth: cardDividerWidth,
      stackPaddingTop: cardInset,
      stackPaddingBottom: cardInset,
      rowGap: cardSectionGap,
      stackedRowGap: cardInset,
      rowPaddingInline: cardInset,
      // Card-space x of every body block's content edge (border + inline inset).
      contentInsetInline: cardBorderWidth + cardInset,
      labelFontSize: labelSize,
      labelLineHeight,
      labelBoxHeight: measuredLabelBoxHeight,
      labelStackHeight,
      labelMarginBottom: cardLabelGap,
      controlFontSize: valueSize,
      controlLineHeight,
      controlBorderWidth,
      inlineControlPaddingBlock,
      inlineControlPaddingInline,
      selectPaddingBlock,
      selectPaddingInline,
      sliderTrackHitArea,
      sliderInlineGap,
      sliderInlineLabelMinWidth,
      sliderInlineLabelWidth,
      sliderInlineTrackMinWidth,
      sliderInlineValueWidth,
      sliderInlineValueHeight,
      sliderInlineControlHeight,
      colorInputSize,
      inlineTextareaMinHeight,
      controlHeights,
    });

    const typography = Object.freeze({
      fontFamily: resolveCssFontFamily(),
      titleSize,
      labelSize,
      valueSize,
      captionSize,
      labelLineHeight,
      controlLineHeight,
    });

    const dimensions = Object.freeze({
      cardRadius: resolveLengthPx('var(--radius-lg)', NODE_LAYOUT_METRIC_FALLBACKS.cardRadius),
      iconSize: resolveLengthPx(
        'var(--ui-dense-control-icon-size)',
        NODE_LAYOUT_METRIC_FALLBACKS.iconSize,
      ),
      previewRadius: resolveLengthPx('var(--radius-md)', NODE_LAYOUT_METRIC_FALLBACKS.previewRadius),
      controlRadiusSm: resolveLengthPx('var(--radius-sm)', NODE_LAYOUT_METRIC_FALLBACKS.controlRadiusSm),
      controlRadiusMd: resolveLengthPx('var(--radius-md)', NODE_LAYOUT_METRIC_FALLBACKS.controlRadiusMd),
      colorInputSize,
      colorInputSwatchRadius: resolveLengthPx(
        'calc(var(--radius-sm) - 2px)',
        NODE_LAYOUT_METRIC_FALLBACKS.colorInputSwatchRadius,
      ),
      borderWidth: controlBorderWidth,
      sliderCompactTrackHeight,
      sliderCompactThumbSize,
      sliderThumbBorderWidth,
    });

    const controls = Object.freeze({
      toggle: Object.freeze({
        trackWidth: toggleCompactTrackWidth,
        trackHeight: toggleCompactTrackHeight,
        thumbSize: toggleCompactThumbSize,
        trackPadding: toggleTrackPadding,
      }),
      referenceChip: Object.freeze({
        height: referenceChipHeight,
        thumbSize: referenceChipThumbSize,
        contentGap: referenceChipContentGap,
        interChipGap: referenceChipInterChipGap,
        thumbRadiusStart: referenceChipThumbRadiusStart,
        thumbRadiusEnd: referenceChipThumbRadiusEnd,
      }),
    });

    const generator = Object.freeze({
      reservedToolbarHeight: generatorReservedToolbarHeight,
      typedPreviewSummaryHeight,
    });

    return Object.freeze({
      spacing: Object.freeze({
        spaceXs,
        spaceSm,
        spaceMd,
        cardInset,
        cardSectionGap,
        cardLabelGap,
      }),
      typography,
      dimensions,
      controls,
      generator,
      row,
    });
  } finally {
    sliderProbe?.remove();
    toggleProbe?.remove();
    referenceChipProbe?.remove();
    referenceChipStripProbe?.remove();
    mediaDropZoneProbe?.remove();
    generatorProbe?.remove();
    selectTriggerProbe?.remove();
    paramLabelProbe?.remove();
    numberInputProbe?.remove();
    sliderValueProbe?.remove();
  }
}

export function getNodeLayoutMetrics() {
  if (!cachedNodeLayoutMetrics) {
    cachedNodeLayoutMetrics = resolveNodeLayoutMetrics();
  }
  return cachedNodeLayoutMetrics;
}

export function warmNodeLayoutMetrics() {
  return getNodeLayoutMetrics();
}

export function __resetNodeLayoutMetricsForTest() {
  cachedNodeLayoutMetrics = null;
}

export function getNodeRowLayoutMetrics() {
  return getNodeLayoutMetrics().row;
}

export function hasVisibleNodeRowLabel(row) {
  return Boolean(row && row.hideLabel !== true && row.label);
}

export function hasStackedNodeRowLabel(row) {
  if (!hasVisibleNodeRowLabel(row)) return false;
  // Both controls paint their label alongside the rail/track, not above it.
  if (row.widget === 'toggle' || row.widget === 'image-num-images-slider') return false;
  return row.widget !== 'slider' || resolveNodeSliderControlLayout(row).mode !== 'inline';
}

export function getNodeRowGap(row) {
  const metrics = getNodeRowLayoutMetrics();
  return hasStackedNodeRowLabel(row) ? metrics.stackedRowGap : metrics.rowGap;
}

export function resolveNodeSliderControlLayout(row) {
  return resolveControlLayout({
    type: 'slider',
    label: hasVisibleNodeRowLabel(row) ? row.label : null,
    orientation: row?.orientation,
    requestedLayout: row?.layout,
    valueDisplay: row?.valueDisplay,
    thumbStyle: row?.thumbStyle,
    fillMode: row?.fillMode,
  });
}

function measureNodeSliderControlHeight(row, metrics) {
  const layout = resolveNodeSliderControlLayout(row);
  const isInline = layout.mode === 'inline';

  return measureControlLayoutHeight(layout, {
    slotHeights: {
      label: metrics.labelLineHeight,
      track: metrics.sliderTrackHitArea,
      // The inline slider's tallest cell is the measured real control (row) box, so
      // the inline row height == the DOM (fixes the ~3px/slider undercount).
      value: isInline ? metrics.sliderInlineControlHeight : metrics.controlLineHeight,
    },
    bandGap: isInline ? 0 : metrics.labelMarginBottom,
  });
}

export function getNodeRowLabelStackHeight(row) {
  if (!hasStackedNodeRowLabel(row)) return 0;
  const metrics = getNodeRowLayoutMetrics();
  // Measured real label box + node margin (falls back to the formula line box in
  // headless environments), so the row height == the real rendered label stack.
  return metrics.labelStackHeight;
}

export function getNodeRowControlHeight(row) {
  const metrics = getNodeRowLayoutMetrics();
  if (isTextAreaControlRow(row)) {
    return Math.max(
      metrics.inlineTextareaMinHeight,
      (getTextAreaRows(row) * metrics.controlLineHeight)
        + (metrics.inlineControlPaddingBlock * 2)
        + (metrics.controlBorderWidth * 2),
    );
  }

  switch (row?.widget) {
    case 'toggle':
      return metrics.controlHeights.toggle;
    case 'select':
      return metrics.controlHeights.select;
    case 'text-input':
    case 'number-input':
      return metrics.controlHeights[row.widget];
    case 'color-input':
    case 'image-drop':
      return metrics.controlHeights[row.widget];
    case 'slider':
      return measureNodeSliderControlHeight(row, metrics);
    default:
      return CONTROL_HEIGHT[row?.widget] || 36;
  }
}

function getPositiveLayoutNumber(value, fallback) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
}

function createControlSlot(paintRect, hitRect = paintRect) {
  return { paintRect, hitRect };
}

function resolveRenderedRowControlRegion(row, {
  nodeWidth,
  rowTop,
  rowHeight,
  scale,
  layoutMetrics,
}) {
  const rowMetrics = layoutMetrics.row;
  const padX = rowMetrics.contentInsetInline * scale;
  const contentWidth = Math.max(0, nodeWidth - padX * 2);
  const rowCenterY = rowTop + rowHeight / 2;
  const labelStackHeight = getNodeRowLabelStackHeight(row) * scale;
  const controlTop = rowTop + labelStackHeight;
  const availableControlHeight = Math.max(0, rowTop + rowHeight - controlTop);

  if (row?.widget === 'slider') {
    const sliderLayout = resolveNodeSliderControlLayout(row);
    const trackPaintHeight = layoutMetrics.dimensions.sliderCompactTrackHeight * scale;
    const trackHitHeight = rowMetrics.sliderTrackHitArea * scale;

    if (sliderLayout.mode === 'inline') {
      const columns = resolveSliderInlineColumnLayout({
        availableWidth: contentWidth,
        gap: rowMetrics.sliderInlineGap * scale,
        labelMinWidth: rowMetrics.sliderInlineLabelMinWidth * scale,
        labelPreferredWidth: rowMetrics.sliderInlineLabelWidth * scale,
        trackMinWidth: rowMetrics.sliderInlineTrackMinWidth * scale,
        valueWidth: rowMetrics.sliderInlineValueWidth * scale,
      });
      const trackX = padX + columns.track.x;
      const valueHeight = rowMetrics.sliderInlineValueHeight * scale;

      return {
        mode: 'inline',
        label: {
          paintRect: {
            x: padX + columns.label.x,
            y: rowCenterY - (rowMetrics.labelLineHeight * scale) / 2,
            width: columns.label.width,
            height: rowMetrics.labelLineHeight * scale,
          },
          hitRect: null,
        },
        track: createControlSlot(
          {
            x: trackX,
            y: rowCenterY - trackPaintHeight / 2,
            width: columns.track.width,
            height: trackPaintHeight,
          },
          {
            x: trackX,
            y: rowCenterY - trackHitHeight / 2,
            width: columns.track.width,
            height: trackHitHeight,
          },
        ),
        value: createControlSlot({
          x: padX + columns.value.x,
          y: rowCenterY - valueHeight / 2,
          width: columns.value.width,
          height: valueHeight,
        }),
      };
    }

    return {
      mode: 'stacked',
      track: createControlSlot(
        {
          x: padX,
          y: controlTop + trackHitHeight / 2 - trackPaintHeight / 2,
          width: contentWidth,
          height: trackPaintHeight,
        },
        {
          x: padX,
          y: controlTop,
          width: contentWidth,
          height: trackHitHeight,
        },
      ),
    };
  }

  if (row?.widget === 'toggle') {
    const toggleMetrics = layoutMetrics.controls.toggle;
    const width = toggleMetrics.trackWidth * scale;
    const height = toggleMetrics.trackHeight * scale;
    return {
      mode: 'inline',
      control: createControlSlot({
        x: padX,
        y: rowCenterY - height / 2,
        width,
        height,
      }),
    };
  }

  if (row?.widget === 'color-input') {
    const size = Math.min(
      layoutMetrics.dimensions.colorInputSize * scale,
      availableControlHeight,
    );
    return {
      mode: 'stacked',
      control: createControlSlot({
        x: padX,
        y: controlTop,
        width: size,
        height: size,
      }),
    };
  }

  if (CONTROL_SURFACE_WIDGETS.has(row?.widget)) {
    const hitHeight = Math.max(
      0,
      Math.min(getNodeRowControlHeight(row) * scale, availableControlHeight),
    );
    const paintHeight = row.widget === 'image-drop' ? 0 : Math.max(2, hitHeight);
    return {
      mode: 'stacked',
      control: createControlSlot(
        paintHeight > 0
          ? { x: padX, y: controlTop, width: contentWidth, height: paintHeight }
          : null,
        { x: padX, y: controlTop, width: contentWidth, height: hitHeight },
      ),
    };
  }

  return null;
}

/**
 * Resolve the rendered row walk and every interactive control region once.
 * Coordinates are in the caller's layout space; raster callers pass their
 * screen scale while hit-testing callers use the default source-space scale.
 */
export function resolveNodeRenderedRowLayouts(node) {
  const rows = Array.isArray(node?.rows) ? node.rows : [];
  const scale = getPositiveLayoutNumber(node?.layoutScale, 1);
  const nodeWidth = getPositiveLayoutNumber(
    node?.layoutWidth ?? node?.canvasWidth ?? node?.bounds?.width ?? node?.width,
    0,
  );
  if (rows.length === 0 || nodeWidth <= 0) return [];

  const layoutMetrics = getNodeLayoutMetrics();
  const rowMetrics = layoutMetrics.row;
  const layouts = [];
  let rowTop = (getNodeRowStackTop() + rowMetrics.stackPaddingTop) * scale;
  let hasRenderedRow = false;

  for (const row of rows) {
    if (!isRenderedNodeRow(row)) continue;

    if (hasRenderedRow) {
      rowTop += getNodeRowGap(row) * scale;
    }

    const rowHeight = getPositiveLayoutNumber(row.height, 0) * scale;
    layouts.push({
      row,
      rowId: row.id,
      widget: row.widget,
      kind: row.kind,
      rowTop,
      rowHeight,
      controlRegion: resolveRenderedRowControlRegion(row, {
        nodeWidth,
        rowTop,
        rowHeight,
        scale,
        layoutMetrics,
      }),
    });

    rowTop += rowHeight;
    hasRenderedRow = true;
  }

  return layouts;
}

function resolveNodeRowHeight(row, explicitHeight) {
  if (row?.widget === 'port-anchor' || row?.widget === 'port-label') {
    return CONTROL_HEIGHT['port-anchor'];
  }

  if (Number.isFinite(explicitHeight) && explicitHeight > 0) {
    return explicitHeight;
  }

  // A composite widget paints stacked labelled fields inside one graph row.
  const stackedFieldRows = Number(row?.graphWidget?.stackedFieldRows);
  if (Number.isInteger(stackedFieldRows) && stackedFieldRows > 0) {
    const metrics = getNodeRowLayoutMetrics();
    return (stackedFieldRows * (metrics.labelStackHeight + metrics.controlHeights.select))
      + ((stackedFieldRows - 1) * metrics.stackedRowGap);
  }

  if (row?.widget === 'slider') {
    return getNodeRowControlHeight(row);
  }

  return getNodeRowLabelStackHeight(row) + getNodeRowControlHeight(row);
}

export function isRenderedNodeRow(row) {
  return row?.widget !== 'port-anchor'
    && row?.widget !== 'port-label'
    && Number(row?.height) > 0;
}

/** Top of the row stack's box (above its padding): card border + header + divider. */
export function getNodeRowStackTop() {
  const metrics = getNodeRowLayoutMetrics();
  return metrics.cardBorderWidth + HEADER_HEIGHT + metrics.dividerWidth;
}

export function computeNodeRowsStackHeight(rows) {
  const renderedRows = Array.isArray(rows) ? rows.filter(isRenderedNodeRow) : [];
  if (renderedRows.length === 0) return 0;

  const metrics = getNodeRowLayoutMetrics();
  return metrics.stackPaddingTop
    + renderedRows.reduce((sum, row) => sum + row.height, 0)
    + renderedRows.slice(1).reduce((sum, row) => sum + getNodeRowGap(row), 0)
    + metrics.stackPaddingBottom;
}

// IDs that hint at multiline text content
const MULTILINE_IDS = /prompt|template|systemPrompt/i;

export function resolveStandardParamWidget(param) {
  // Explicit options array → select
  if (Array.isArray(param.options) && param.options.length > 0) {
    return 'select';
  }
  // Media entries of any type share the one drop zone.
  if (param.mediaEntries === true) return 'image-drop';

  switch (param.type) {
    case PortType.IMAGE:
      return 'image-drop';

    case PortType.TEXT:
      if (MULTILINE_IDS.test(param.id)) return 'textarea';
      if (typeof param.default === 'string' && param.default.length > 50) return 'textarea';
      return 'text-input';

    case PortType.NUMBER:
      if (param.min != null && param.max != null) return 'slider';
      return 'number-input';

    case PortType.BOOLEAN:
      return 'toggle';

    case PortType.COLOR:
      return 'color-input';

    default:
      return 'text-input';
  }
}

/**
 * Resolve the widget type for a param definition.
 */
function resolveParamWidget(param) {
  return param.graphWidget?.id ?? resolveStandardParamWidget(param);
}

/**
 * Resolve the widget type for an input port row.
 */
function resolveInputWidget(input, nodeDef) {
  if (input.graphWidget?.id) {
    return input.graphWidget.id;
  }
  if (nodeDef.isDropZone && input.type === PortType.IMAGE) return 'image-drop';
  return 'port-label';
}

/**
 * Build an ordered array of row descriptors from a node definition.
 *
 * Row ordering: outputs → inputs → definition → params.
 *
 * @param {Object} nodeDef - Node definition from the registry
 * @returns {Array<Object>} Row descriptors
 */
export function buildNodeRows(nodeDef) {
  if (!nodeDef) return [];

  const rows = [];

  // Output rows
  for (const output of nodeDef.outputs || []) {
    rows.push({
      id: output.id,
      kind: 'output',
      side: 'right',
      portType: output.type,
      label: output.label || output.id,
      widget: 'port-anchor',
      height: CONTROL_HEIGHT['port-anchor'],
      multiple: output.multiple === true,
      ...(nodeDef.nodePresentation?.portPlacement
        ? { placement: nodeDef.nodePresentation.portPlacement }
        : {}),
    });
  }

  // Input rows
  for (const input of nodeDef.inputs || []) {
    const widget = resolveInputWidget(input, nodeDef);
    const isAnchorOnly = widget === 'port-label';
    const resolvedWidget = isAnchorOnly ? 'port-anchor' : widget;
    rows.push({
      id: input.id,
      kind: 'input',
      side: 'left',
      portType: input.type,
      label: input.label || input.id,
      widget: resolvedWidget,
      height: resolveNodeRowHeight({
        widget: resolvedWidget,
        label: input.label || input.id,
        hideLabel: true,
        graphWidget: input.graphWidget,
        rows: input.rows,
        adaptiveRows: input.adaptiveRows,
      }, input.graphWidget?.height),
      hideLabel: true,
      multiple: input.multiple === true,
      ...(nodeDef.nodePresentation?.portPlacement
        ? { placement: nodeDef.nodePresentation.portPlacement }
        : {}),
      ...(input.required != null && { required: input.required }),
      ...(input.placeholder && { placeholder: input.placeholder }),
      ...(input.default != null && { defaultValue: input.default }),
      ...(input.options && { options: input.options }),
      ...(input.min != null && { min: input.min }),
      ...(input.max != null && { max: input.max }),
      ...(input.step != null && { step: input.step }),
      ...(input.graphWidget && { graphWidget: { ...input.graphWidget } }),
      ...(input.perConnection && { perConnection: { ...input.perConnection } }),
      ...(input.inactiveWhenParamIn && { inactiveWhenParamIn: { ...input.inactiveWhenParamIn } }),
    });
  }

  // Definition row: which definition this node runs (today the Effect node's effect). Its
  // id is the `nodeId` data key itself, so every surface reads the same stored identity and
  // the choice is never a param an executor, catalog, or workflow interface can see.
  if (nodeDef.definitionChoice) {
    const { label, options } = nodeDef.definitionChoice;
    rows.push({
      id: NODE_DEFINITION_ROW_ID,
      kind: 'definition',
      side: null,
      portType: null,
      label,
      widget: 'select',
      height: resolveNodeRowHeight({ widget: 'select', label }),
      options,
    });
  }

  // Param rows
  for (const param of nodeDef.params || []) {
    if (param.graphHidden === true) {
      continue;
    }

    const widget = resolveParamWidget(param);
    rows.push({
      id: param.id,
      kind: 'param',
      side: null,
      portType: null,
      label: param.label || param.id,
      widget,
      height: resolveNodeRowHeight({
        widget,
        label: param.label || param.id,
        hideLabel: param.hideLabel === true,
        graphWidget: param.graphWidget,
        rows: param.rows,
        adaptiveRows: param.adaptiveRows,
      }, param.graphWidget?.height),
      // Pass through widget-specific metadata
      ...(param.hideLabel === true && { hideLabel: true }),
      ...(param.min != null && { min: param.min }),
      ...(param.max != null && { max: param.max }),
      ...(param.step != null && { step: param.step }),
      ...(param.options && { options: param.options }),
      ...(param.default != null && { defaultValue: param.default }),
      ...(param.graphWidget && { graphWidget: { ...param.graphWidget } }),
      ...(param.inactiveWhenParamIn && { inactiveWhenParamIn: { ...param.inactiveWhenParamIn } }),
      ...(param.graphUnavailable && { graphUnavailable: { ...param.graphUnavailable } }),
      ...getMediaEntryParamFacts(param),
    });
  }

  return rows;
}

/**
 * Compute the total node height from its rows.
 *
 * @param {Array} rows - Row descriptors from buildNodeRows
 * @returns {number} Total height in pixels
 */
export function computeNodeHeight(rows) {
  const metrics = getNodeRowLayoutMetrics();
  const stackHeight = computeNodeRowsStackHeight(rows);
  const total = stackHeight > 0
    ? getNodeRowStackTop() + stackHeight + metrics.cardBorderWidth
    : HEADER_HEIGHT + metrics.cardBorderWidth * 2;
  // Minimum height when no rows
  return Math.max(total, HEADER_HEIGHT + 20);
}
